import { Link } from "react-router-dom"
import { Users, Cpu, LineChart, Truck, Shield, ListChecks, ArrowRight } from "lucide-react"
import { useLang } from "../context/LangContext"
import { useAuth } from "../context/AuthContext"
import ToolsSection from "../components/ToolsSection"

const CONTENT = {
  en: {
    badge: "Social platform for entrepreneurs",
    heroH1: "The ecosystem where ideas become high-impact businesses.",
    heroSub: "uCloud360 is the digital incubation and acceleration platform for Hispanic entrepreneurs in the United States. Tools, community, mentorship — everything in one place.",
    ctaPrimary: "Join free",
    ctaSecondary: "Learn more",
    ctaDashboard: "Go to Dashboard",
    communityTitle: "Specialty channels",
    communitySub: "Expert-to-expert knowledge in the areas that matter most.",
    channels: [
      { name: "Tech", icon: Cpu, desc: "Digital infrastructure & development" },
      { name: "Finance", icon: LineChart, desc: "Investment & capital strategy" },
      { name: "Logistics", icon: Truck, desc: "Supply chain & fulfillment" },
      { name: "Legal", icon: Shield, desc: "Contracts, IP & compliance" },
      { name: "Procedures", icon: ListChecks, desc: "Operations & project management" },
    ],
    memberLabel: "Subscribers only",
    closeBadge: "Ready to grow?",
    closeH2: "Your network is your most valuable business asset.",
    closeBody: "Join a community of entrepreneurs who are already solving the challenges you face — and sharing the expertise you have.",
    closeCta: "Create your account",
    closeSecondary: "See plans",
    statsLabel: "Platform",
    stats: [
      { label: "Specialty channels", value: "5" },
      { label: "Built-in tools", value: "4" },
      { label: "Mentorship model", value: "1:1" },
    ],
  },
  es: {
    badge: "Red social para emprendedores",
    heroH1: "El ecosistema donde las ideas se convierten en empresas de alto impacto.",
    heroSub: "uCloud360 es la plataforma de incubación y aceleración digital para emprendedores hispanos en los Estados Unidos. Herramientas, comunidad y mentoría — todo en un solo lugar.",
    ctaPrimary: "Únete gratis",
    ctaSecondary: "Saber más",
    ctaDashboard: "Ir al Dashboard",
    communityTitle: "Canales de especialidad",
    communitySub: "Conocimiento experto en las áreas que más importan.",
    channels: [
      { name: "Tech", icon: Cpu, desc: "Infraestructura digital y desarrollo" },
      { name: "Finanzas", icon: LineChart, desc: "Inversión y estrategia de capital" },
      { name: "Logística", icon: Truck, desc: "Cadena de suministro y almacenamiento" },
      { name: "Legal", icon: Shield, desc: "Contratos, PI y cumplimiento normativo" },
      { name: "Procedimientos", icon: ListChecks, desc: "Operaciones y gestión de proyectos" },
    ],
    memberLabel: "Solo suscriptores",
    closeBadge: "¿Listo para crecer?",
    closeH2: "Tu red es tu activo empresarial más valioso.",
    closeBody: "Únete a una comunidad de emprendedores que ya están resolviendo los desafíos que tú enfrentas — y compartiendo la experiencia que tú tienes.",
    closeCta: "Crear tu cuenta",
    closeSecondary: "Ver planes",
    statsLabel: "Plataforma",
    stats: [
      { label: "Canales de especialidad", value: "5" },
      { label: "Herramientas incluidas", value: "4" },
      { label: "Modelo de mentoría", value: "1:1" },
    ],
  },
}

export default function HomePage() {
  const { lang } = useLang()
  const { user } = useAuth()
  const t = CONTENT[lang]
  const isLoggedIn = !!user

  const card = "bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-gray-800"
  const muted = "text-gray-500 dark:text-gray-400"
  const mutedBody = "text-gray-600 dark:text-gray-300"
  const primaryBtn = "inline-flex items-center gap-2 rounded-xl bg-[#2563EB] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1d4ed8] transition"
  const secondaryBtn = "inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-gray-700 px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"

  const heroSection = (
    <section className="max-w-6xl mx-auto px-6 pt-16 pb-14">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1 text-xs text-gray-500 dark:text-gray-400 mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
          {t.badge}
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1]">
          {t.heroH1}
        </h1>
        <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${mutedBody}`}>
          {t.heroSub}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {isLoggedIn ? (
            <Link to="/app/dashboard" className={primaryBtn}>
              {t.ctaDashboard} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <>
              <Link to="/register" className={primaryBtn}>
                {t.ctaPrimary} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/about" className={secondaryBtn}>
                {t.ctaSecondary}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="mt-14 flex flex-wrap gap-8">
        {t.stats.map((s) => (
          <div key={s.label}>
            <div className="text-3xl font-semibold tracking-tight">{s.value}</div>
            <div className={`text-sm mt-0.5 ${muted}`}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  )

  const toolsSection = (
    <ToolsSection className="max-w-6xl mx-auto px-6 py-14 border-t border-gray-100 dark:border-gray-800" />
  )

  return (
    <div className="bg-white dark:bg-black text-black dark:text-white">

      {isLoggedIn ? (
        <>
          {toolsSection}
          {heroSection}
        </>
      ) : (
        <>
          {heroSection}
          {toolsSection}
        </>
      )}

      {/* ── COMMUNITY CHANNELS ───────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-14 border-t border-gray-100 dark:border-gray-800">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{t.communityTitle}</h2>
            <p className={`mt-2 text-sm ${muted}`}>{t.communitySub}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1 text-xs text-gray-400">
            <Users className="h-3 w-3" />
            {t.memberLabel}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {t.channels.map((ch) => (
            <div key={ch.name} className={`${card} p-6`}>
              <ch.icon className="h-5 w-5 text-gray-400 dark:text-gray-500 mb-4" />
              <div className="font-medium text-sm">{ch.name}</div>
              <p className={`mt-1.5 text-xs ${muted} leading-relaxed`}>{ch.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CLOSE CTA ────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-14 border-t border-gray-100 dark:border-gray-800">
        <div className={`${card} p-10 md:p-14`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="max-w-xl">
              <div className={`text-xs font-medium tracking-widest uppercase ${muted} mb-3`}>{t.closeBadge}</div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight">{t.closeH2}</h2>
              <p className={`mt-4 ${mutedBody} leading-relaxed`}>{t.closeBody}</p>
            </div>
            <div className="flex flex-col gap-3 flex-shrink-0">
              <Link to="/register" className={primaryBtn}>
                {t.closeCta} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className={secondaryBtn}>
                {t.closeSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="h-10" />
    </div>
  )
}
